import type { Frame } from './types.ts'

/** One importable frame of a Figma file, as listed for review. */
export interface FigmaFrameRef {
  id: string
  name: string
  /** the Figma page it sits on; '' when the link named a single layer */
  page: string
  width: number
  height: number
}

export interface FigmaFileListing {
  fileKey: string
  fileName: string
  frames: FigmaFrameRef[]
  truncated: boolean
  /** the link pointed at a page, section or layer rather than the whole file */
  focused: boolean
}

/** The import route's answer: the job has started. It carries on as the
 *  "Figma Import" task on the canvas, which is where its outcome shows. */
export interface FigmaImportStarted {
  taskId: string
  frames: number
}

/** What an import came to — returned to agents, which wait for it. */
export interface FigmaImportResult {
  frames: Frame[]
  failures: { id: string; error: string }[]
  /** frames too heavy for HTML that landed as Figma's render instead */
  flattened: number
  /** images that could not be copied into doop (too large, or unreachable) and
   *  still point at Figma's own URL, which stops working after a few weeks */
  expiring: number
  /** families the text names that neither Google Fonts nor the canvas's font
   *  library carries: the text stays editable in that family and renders in a
   *  fallback until a face is uploaded */
  missingFonts: string[]
}

/** The Agents-panel worker a running import shows up as — a named job with a
 *  task and a progress bar, but no one's AI (see shared/agents.ts). */
export const FIGMA_IMPORT_WORKER = 'Figma Import'

/** A Figma pull step in an automation: each run lists the link's frames and
 *  imports the ones not yet on the canvas (matched by the frame marker). */
export interface FigmaPullStep {
  id: string
  type: 'pull'
  provider: 'figma'
  /** a figma.com/design/… link — a file, page, section or frame */
  url: string
  canvasId: string
}

/** A cheap shape check for the automation editor: real parsing (branches,
 *  node ids, refused board/slides links) stays on the server. */
export function looksLikeFigmaLink(url: string): boolean {
  return /figma\.com\/(design|file|proto)\/[A-Za-z0-9]{5,}/.test(url)
}
